/**
 * What the train's screens need computed from the session (backend/backend/domains/trains/spec.yaml `ui:` names them
 * with `build:` and `action:`): the views and the bar actions that send what the user put together on the screen.
 * Named by role, never by the screen that shows them (docs/NEW-UI/05-spec-ui.md §2).
 */
import { formatMoney, locale } from '@platform/i18n/translator';
import { defineBuilder, type ActionDef, type ScreenBuilder, type ScreenContext } from '@platform/packs/contract';
import { classRows } from './fares';
import { readTrip, type Trip } from './session';
import type { Journey } from './widgets/trip';
import { changesOf, itineraryOf } from './widgets/itinerary';
import type { SeatDraft } from './widgets/SeatMap';

/** «Next» with what the screen put together (the counters, the seats chosen on the map). */
const next = (label: string, data: Record<string, unknown> = {}): ActionDef =>
  ({ id: 'next', label, icon: 'next', press: { button: 'confirm', data }, tone: 'primary', place: 'end' });

const tripOf = (ctx: ScreenContext) => readTrip(ctx.session);

function day(trip: Trip, language: string): string {
  if (!trip.date) return '';
  const d = new Date(`${trip.date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? trip.date : d.toLocaleDateString(locale(language), { weekday: 'short', day: 'numeric', month: 'short' });
}

/** The date and the time asked for (before a train is chosen). */
function when(trip: Trip, language: string): string {
  return [day(trip, language), trip.time].filter(Boolean).join(' · ');
}

const route = (trip: Trip) => `${trip.origin ?? '—'} → ${trip.destination ?? '—'}`;

/** Counters start from the session; what the user changed is in the draft. */
function passengersPayload(ctx: ScreenContext) {
  const trip = tripOf(ctx);
  const n = (key: string, fallback: number) => (typeof ctx.draft[key] === 'number' ? (ctx.draft[key] as number) : fallback);
  return {
    adults: n('adults', trip.adults),
    children: n('children', trip.children),
    extras: {
      petsSmall: n('petsSmall', trip.extras.petsSmall), petsBig: n('petsBig', trip.extras.petsBig),
      bikesNormal: n('bikesNormal', trip.extras.bikesNormal), bikesFoldable: n('bikesFoldable', trip.extras.bikesFoldable),
    },
  };
}

/** The assistance tiles of the passenger card: each one asks the backend for an accessibility mode. */
const ASSISTANCE = [
  { mode: 'motor', icon: 'accessibility', label: 'trains.passengers.assist_motor' },
  { mode: 'visual', icon: 'eye', label: 'trains.passengers.assist_visual' },
  { mode: 'hearing', icon: 'ear', label: 'trains.passengers.assist_hearing' },
] as const;

/** Who travels: people, pets and bikes, with the assistance the kiosk can switch on. */
const partyCounters = defineBuilder('counters', (ctx) => {
  const trip = tripOf(ctx);
  const { t } = ctx;
  const mode = ctx.session.accessibility.mode;
  return {
    widget: 'counters',
    variant: 'cards', // the old kiosk's passenger card
    props: {
      title: t('trains.passengers.title'),
      subtitle: `${route(trip)} · ${when(trip, ctx.language)}`,
      icon: 'users',
      groups: [
        { id: 'people', style: 'row' },
        { id: 'extras', title: t('trains.passengers.extras'), icon: 'sparkles', style: 'tiles' },
      ],
      items: [
        { id: 'adults', group: 'people', label: t('trains.passengers.adults'), sub: t('trains.passengers.adults.sub'), icon: 'user', min: 1, max: 9, value: trip.adults },
        { id: 'children', group: 'people', label: t('trains.passengers.children'), sub: t('trains.passengers.children.sub'), icon: 'child', min: 0, max: 9, value: trip.children },
        { id: 'petsSmall', group: 'extras', label: t('trains.passengers.pets_small'), icon: 'dog', min: 0, max: 2, value: trip.extras.petsSmall },
        { id: 'petsBig', group: 'extras', label: t('trains.passengers.pets_big'), icon: 'dog', min: 0, max: 2, value: trip.extras.petsBig },
        { id: 'bikesNormal', group: 'extras', label: t('trains.passengers.bikes'), icon: 'bike', min: 0, max: 2, value: trip.extras.bikesNormal },
      ],
      togglesTitle: t('trains.passengers.assistance'),
      togglesIcon: 'accessibility',
      toggles: ctx.can('set_accessibility')
        ? ASSISTANCE.map((a) => ({
          id: a.mode, label: t(a.label), icon: a.icon, selected: mode === a.mode,
          intent: { press: { button: 'set_accessibility', data: { mode: mode === a.mode ? 'none' : a.mode } } },
        }))
        : [],
    },
  };
});

/** The trains found, the page shown and the one chosen. */
const trainList = defineBuilder('train-results', (ctx) => {
  const trip = tripOf(ctx);
  return { widget: 'train-results', props: { trains: trip.results, page: trip.pageIdx, selectedIdx: trip.selectedIdx } };
});

function seatsPayload(ctx: ScreenContext): Record<string, unknown> {
  const draft = ctx.draft as SeatDraft;
  return draft.seats?.length ? { seats: draft.seats, coach: draft.coach } : {};
}

/** The coaches of the chosen train, the seats to pick; a note when the train sells none. */
const seatChoice = defineBuilder('seat-map', (ctx) => {
  const trip = tripOf(ctx);
  const train = trip.selectedTrain;
  if (trip.coaches.length === 0) return { widget: 'message', props: { tone: 'info', icon: 'seat', title: ctx.t('trains.seats.none') } };
  return {
    widget: 'seat-map',
    props: {
      title: ctx.t('trains.seats.title'),
      subtitle: train ? ctx.t('trains.seats.subtitle', { train: train.train_id, from: train.departure_station, to: train.arrival_station }) : undefined,
      coaches: trip.coaches,
      page: trip.pageIdx,
      needed: Math.max(1, trip.adults + trip.children),
      assigned: trip.seats,
      destination: train?.arrival_station,
    },
  };
});

/** Who buys and who travels: a field per name, the email, the phone. */
const travellerForm = defineBuilder('form', (ctx) => {
  const trip = tripOf(ctx);
  const { t } = ctx;
  const field = (id: string, value: string | null, kind: 'text' | 'email' | 'tel', required: boolean, data: Record<string, unknown>) => ({
    id, label: t(`trains.data.${id}`), value, kind, required, sensitivity: 'personal' as const, send: { button: 'set_data', data },
  });
  const others = Array.from({ length: Math.max(0, trip.adults + trip.children - 1) }, (_, i) => {
    const details = trip.passengersDetails[i + 1] ?? {};
    return [
      { ...field(`p${i + 1}_first`, details.first_name ?? null, 'text', true, { field: 'passenger_first_name', passenger_index: i + 1 }), label: t('trains.data.passenger_first_name', { n: i + 2 }) },
      { ...field(`p${i + 1}_last`, details.last_name ?? null, 'text', true, { field: 'passenger_last_name', passenger_index: i + 1 }), label: t('trains.data.passenger_last_name', { n: i + 2 }) },
    ];
  }).flat();
  return {
    widget: 'form',
    props: {
      title: t('trains.data.title'),
      subtitle: trip.selectedTrain ? t('trains.data.subtitle', { train: trip.selectedTrain.train_id, pax: trip.adults + trip.children }) : undefined,
      icon: 'edit',
      fields: [
        field('first_name', trip.orderer.first_name, 'text', true, { field: 'first_name' }),
        field('last_name', trip.orderer.last_name, 'text', true, { field: 'last_name' }),
        { ...field('email', trip.orderer.email, 'email', true, { field: 'email' }), wide: true },
        field('phone', trip.orderer.phone, 'tel', false, { field: 'phone' }),
        ...others,
      ],
      doneLabel: t('keyboard.done'),
      spaceLabel: t('keyboard.space'),
      footer: t('trains.data.footer'),
    },
  };
});

/** The journey the old kiosk's cards read (widgets/trip.ts): the chosen train, its legs, the people and seats. */
function journeyOf(trip: Trip): Journey | null {
  const train = trip.selectedTrain;
  if (!train) return null;
  return {
    trainType: train.train_type, trainNumber: train.train_id,
    departure: train.departure_time, arrival: train.arrival_time,
    origin: train.departure_station, destination: train.arrival_station,
    // the legs and their count as the results card has them (widgets/itinerary.ts): one source for every look
    durationMin: train.duration_min, changes: changesOf(train),
    legs: itineraryOf(train).legs,
    travelClass: trip.selectedClass, seats: trip.seats, adults: trip.adults, children: trip.children, extras: trip.extras,
    onBoard: train.on_board,
    idx: trip.selectedIdx,
  };
}

/** The old kiosk's look for the summary and the ticket: the train's suggestion, Settings may choose another (07 D-03). */
const CLASSIC = 'classic';

/** The booking to pay: train, route, time, class, seats, people, extras and the total. */
const tripSummary = defineBuilder('trip-summary', (ctx) => {
  const trip = tripOf(ctx);
  const { t, language } = ctx;
  const train = trip.selectedTrain;
  const extras = Object.entries(trip.extras).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${t(`trains.passengers.${{ petsSmall: 'pets_small', petsBig: 'pets_big', bikesNormal: 'bikes', bikesFoldable: 'bikes_foldable' }[k]}`)}`);
  const price = trip.totalPrice ?? (train ? (classRows(train.class_prices).find((r) => r.name === trip.selectedClass)?.price ?? train.price_eur) * (trip.adults + trip.children) : null);
  const rows = [
    train && { label: t('trains.summary.train'), value: `${train.train_type} ${train.train_id}`, icon: 'train' },
    { label: t('trains.summary.route'), value: route(trip), icon: 'map' },
    train && { label: t('trains.summary.when'), value: `${day(trip, language)} · ${train.departure_time} → ${train.arrival_time}`, icon: 'clock' },
    trip.selectedClass && { label: t('trains.summary.class'), value: [trip.selectedClass, trip.selectedFare].filter(Boolean).join(' · '), icon: 'seat' },
    trip.seats.length > 0 && { label: t('trains.summary.seats'), value: trip.seats.map((s) => t('trains.summary.seat_in_coach', { seat: s.seat, coach: s.coach })).join(', '), icon: 'seat' },
    { label: t('trains.summary.passengers'), value: String(trip.adults + trip.children), icon: 'users' },
    extras.length > 0 && { label: t('trains.summary.extras'), value: extras.join(', '), icon: 'dog' },
  ].filter(Boolean);
  const view = {
    title: t('trains.summary.title'),
    icon: 'ticket',
    rows,
    total: price !== null ? { label: t('trains.summary.total'), value: formatMoney(price, language) } : undefined,
  };
  const journey = journeyOf(trip);
  if (!journey) return { widget: 'summary', props: view };
  const pax = Math.max(1, trip.adults + trip.children);
  return { widget: 'trip-summary', variant: CLASSIC, props: { view, trip: { ...journey, unitPrice: price === null ? null : price / pax, total: price } } };
});

/** The ticket bought: the PNR, the journey, the coach and seats, the QR to show on board. */
const tripTicket = defineBuilder('trip-ticket', (ctx) => {
  const trip = tripOf(ctx);
  const { t, language } = ctx;
  const train = trip.selectedTrain;
  const coach = trip.seats[0]?.coach ?? trip.carriage;
  const qr = trip.pnr ? JSON.stringify({ pnr: trip.pnr, cp: trip.cp, train: train ? `${train.train_type} ${train.train_id}` : null, from: trip.origin, to: trip.destination, date: trip.date, departure: train?.departure_time, coach, seats: trip.seats.map((s) => s.seat) }) : undefined;
  const view = {
    title: t('trains.done.title'),
    subtitle: t('trains.done.subtitle'),
    code: trip.pnr ? { label: t('trains.done.pnr'), value: trip.pnr } : undefined,
    lines: [
      train && { label: t('trains.summary.train'), value: `${train.train_type} ${train.train_id}`, icon: 'train' },
      train && { label: t('trains.summary.when'), value: `${day(trip, language)} · ${train.departure_time}`, icon: 'clock' },
      { label: t('trains.summary.route'), value: route(trip), icon: 'map', wide: true },
      coach && { label: t('trains.done.coach'), value: String(coach), icon: 'seat' },
      trip.seats.length > 0 && { label: t('trains.summary.seats'), value: trip.seats.map((s) => s.seat).join(', '), icon: 'seat' },
      train?.platform && { label: t('trains.done.platform'), value: train.platform, icon: 'info' },
      trip.cp && { label: t('trains.done.cp'), value: trip.cp, icon: 'qr' },
    ].filter(Boolean),
    qr,
    qrLabel: t('trains.done.qr'),
    note: t('trains.done.note'),
  };
  const journey = journeyOf(trip);
  if (!journey) return { widget: 'ticket', props: view };
  return {
    widget: 'trip-ticket', variant: CLASSIC,
    props: { view, trip: { ...journey, total: trip.totalPrice, coach: coach ?? null, platform: train?.platform ?? null, pnr: trip.pnr, cp: trip.cp, qr } },
  };
});

export const TRAINS_BUILDERS: Record<string, ScreenBuilder> = {
  party_counters: partyCounters,
  train_list: trainList,
  seat_choice: seatChoice,
  traveller_form: travellerForm,
  trip_summary: tripSummary,
  trip_ticket: tripTicket,
};

export const TRAINS_ACTIONS: Record<string, (ctx: ScreenContext) => ActionDef> = {
  /** Search with the counters as the user left them. */
  send_party: (ctx) => next('trains.action.search', passengersPayload(ctx)),
  /** Go on with the seats picked on the map (none: the backend assigns them). */
  send_seats: (ctx) => next('trains.action.next', seatsPayload(ctx)),
};
